import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Button, SortDirection, Table } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import DeleteTaxRateModal from 'modules/finance/components/DeleteTaxRateModal'
import EmptyTableBody from 'modules/finance/components/EmptyTableBody'
import { TaxRateFragment, useTaxTypes } from 'modules/finance/hooks/useTaxTypes'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import NewTaxRateModal from '../NewTaxRateModal'
import TaxTableHeader from './Header'
import TaxValueRow from './TaxValueRow'

const sortTaxRates = createComputed(
  (taxRates: TaxRateFragment[] | undefined, attribute: string, direction: 'asc' | 'desc') =>
    orderBy(
      taxRates,
      (taxRate) => {
        switch (attribute) {
          case 'value': {
            return taxRate.rate
          }
          case 'description': {
            return taxRate.description
          }
          default: {
            return attribute
          }
        }
      },
      [direction]
    )
)

interface Props {
  taxTypeId: number
}

const TaxType = ({ taxTypeId }: Props) => {
  const { data, loading, error } = useTaxTypes([taxTypeId])
  const taxType = data?.finance?.taxTypes?.at(0)
  const [showNewModal, setShowNewModal] = React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)
  const [selectedTaxRateId, setSelectedTaxRateId] = React.useState<number | null>(null)

  /*
   * Column sort props
   */
  const [sortDirection, setDirection] = React.useState<SortDirection>('asc')
  const [sortAttribute, setSortAttribute] = React.useState<string>('value')

  const onSort = (sortAttribute: string, sortDirection: SortDirection) => {
    setDirection(sortDirection)
    setSortAttribute(sortAttribute)
  }

  const showDeleteModalForTaxRateId = (id: number) => {
    setSelectedTaxRateId(id)
    setShowDeleteModal(true)
  }

  const taxRates = taxType?.taxRates
  const sortedTaxRates = sortTaxRates(taxRates, sortAttribute, sortDirection).get()

  return (
    <>
      <ResponsivePage.Body loading={loading} error={error}>
        <Box width="full">
          <Box paddingY="s8" alignSelf="end">
            <Button iconName="add" type="secondary" onClick={() => setShowNewModal(true)}>
              {i18n.t('finance.settings.taxes.tax_type.buttons.add')}
            </Button>
          </Box>
          <Table fullWidth>
            <TaxTableHeader onSort={onSort} direction={sortDirection} attribute={sortAttribute} />
            {sortedTaxRates.length > 0 ? (
              <Table.Body>
                {sortedTaxRates?.map((taxRate) => (
                  <TaxValueRow
                    key={taxRate.id}
                    taxRate={taxRate}
                    onDelete={() => showDeleteModalForTaxRateId(taxRate.id)}
                  />
                ))}
              </Table.Body>
            ) : (
              <EmptyTableBody
                message={i18n.t('finance.settings.taxes.tax_type.no_tax_rates_added')}
                colSpan={4}
              />
            )}
          </Table>
        </Box>

        {showNewModal && (
          <NewTaxRateModal taxTypeId={taxTypeId} onClose={() => setShowNewModal(false)} />
        )}
        {showDeleteModal && selectedTaxRateId && (
          <DeleteTaxRateModal
            taxTypeId={taxTypeId}
            taxRateId={selectedTaxRateId}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
      </ResponsivePage.Body>
      <Outlet />
    </>
  )
}

export default TaxType
